import React, { useState } from "react";
import { SectionCard, EmptyState } from "@/components/common/ui";
import { formatCount, formatMoney } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Users, Eye, Home, ArrowRight, Gauge, Building2, Target } from "lucide-react";
import { FunnelEditorDialog } from "@/components/dashboard/FunnelEditorDialog";

const DASH = "\u2014";

// Leads flow is exactly Leads → Site Visit → Booking (per requirement). Qualified
// Leads is used only for the Cost / Qualified Lead ratio below.
const STAGES = [
  { key: "leads", label: "Leads", icon: Users, accent: "hsl(var(--chart-cat-4))" },
  { key: "sv", label: "Site Visits", icon: Eye, accent: "hsl(var(--chart-cat-3))" },
  { key: "booking", label: "Bookings", icon: Home, accent: "hsl(var(--success))" },
];

const pct = (v) => (v === null || v === undefined ? DASH : `${(v * 100).toFixed(1)}%`);
// Conversions read left→right in the same direction as the label, e.g.
// "Lead → Site Visit · 20 : 1" = 20 leads convert into 1 site visit.
// (The underlying math is unchanged: v = outcome ÷ input, e.g. SV ÷ Leads.)
const ratio = (v) => {
  if (v === null || v === undefined || v <= 0) return DASH;
  if (v >= 1) return "1 : 1";
  return `${Math.round(1 / v).toLocaleString("en-IN")} : 1`;
};

function StageCard({ stage, actual, target }) {
  const Icon = stage.icon;
  const att = target ? Math.min(100, Math.round((actual / target) * 100)) : null;
  return (
    <div className="flex-1 rounded-xl border bg-card p-3" data-testid={`funnel-stage-${stage.key}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <span className="flex h-6 w-6 items-center justify-center rounded-md" style={{ background: `${stage.accent}1f`, color: stage.accent }}><Icon className="h-3.5 w-3.5" /></span>
          {stage.label}
        </span>
        {att !== null && <Badge variant="outline" className="text-[9px] tabular-nums">{att}%</Badge>}
      </div>
      <p className="mt-2 font-heading text-2xl font-semibold tabular-nums" style={{ color: stage.accent }} data-testid={`funnel-stage-${stage.key}-actual`}>{formatCount(actual)}</p>
      <p className="text-[10px] text-muted-foreground">of {target ? formatCount(target) : DASH} target</p>
    </div>
  );
}


export function CostEfficiency({ data, isFull, onSaved }) {
  const [vmode, setVmode] = useState("ratios"); // ratios | costs
  const ff = data.funnel_flow || {};
  const ratios = data.cost_ratios || { by_vertical: [], overall: {}, by_project: [] };
  const stageVals = {
    leads: [ff.leads_actual || 0, ff.leads_target || 0],
    sv: [ff.sv_actual || 0, ff.sv_target || 0],
    booking: [ff.booking_actual || 0, ff.booking_target || 0],
  };
  const conv = [ff.lead_to_sv, ff.sv_to_booking];
  const rows = ratios.by_vertical || [];
  const overall = ratios.overall || {};
  const projRows = ratios.by_project || [];
  const m = (v) => (v === null || v === undefined ? DASH : formatMoney(v, "crore"));
  const share = (v) => (v === null || v === undefined ? DASH : `${(v * 100).toFixed(1)}%`);

  const costMetrics = [
    { key: "cpl", label: "Cost / Lead" },
    { key: "cpql", label: "Cost / Qual. Lead" },
    { key: "cpsv", label: "Cost / Site Visit" },
    { key: "cpb", label: "Cost / Booking" },
  ];
  const ratioMetrics = [
    { key: "spend_share", label: "Spend Share", fmt: share },
    { key: "lead_to_sv", label: "Lead → SV", fmt: ratio },
    { key: "sv_to_booking", label: "SV → Booking", fmt: ratio },
    { key: "lead_to_booking", label: "Lead → Booking", fmt: ratio },
  ];

  return (
    <SectionCard title="Leads Flow & Cost Efficiency"
      description="Leads → Site Visit → Booking with conversion ratios. Cost-per-outcome uses performance spends only and can be toggled on the vertical table below."
      action={isFull ? <FunnelEditorDialog onSaved={onSaved} projects={data.scope_projects || data.curated || []} /> : null}
      testId="module-funnel">
      {/* funnel rail */}
      <div className="border-b p-4">
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center" data-testid="funnel-rail">
          {STAGES.map((s, i) => (
            <React.Fragment key={s.key}>
              <StageCard stage={s} actual={stageVals[s.key][0]} target={stageVals[s.key][1]} />
              {i < STAGES.length - 1 && (
                <div className="flex shrink-0 flex-col items-center justify-center px-1 text-muted-foreground" data-testid={`funnel-conv-${i}`}>
                  <ArrowRight className="hidden h-4 w-4 sm:block" />
                  <span className="text-[9px] tabular-nums">{ratio(conv[i])}</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* project-level conversion ratio table */}
      <div className="border-b p-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> Conversion ratios by project</p>
        {projRows.length === 0 ? (
          <EmptyState title="No project funnel data" message="Add leads / site visits / bookings per project to compute conversion ratios." />
        ) : (
          <>
            <div className="table-scroll">
              <table className="w-full border-collapse" data-testid="project-conversion-matrix">
                <thead><tr>
                  {["Project", "Leads", "Site Visits", "Bookings", "Lead → SV", "SV → Booking", "Lead → Booking", "Perf. Spend"].map((h, i) => (
                    <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${i === 0 ? "th-sticky-left text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {projRows.map((r, i) => (
                    <tr key={r.project} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`} data-testid={`project-conversion-row-${r.project}`}>
                      <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">{r.project}</td>
                      <td className="px-3 py-2.5 num text-sm text-muted-foreground">{formatCount(r.leads)}</td>
                      <td className="px-3 py-2.5 num text-sm text-muted-foreground">{formatCount(r.site_visits)}</td>
                      <td className="px-3 py-2.5 num text-sm text-muted-foreground">{formatCount(r.bookings)}</td>
                      <td className="px-3 py-2.5 num text-sm font-medium" data-testid={`project-conv-lead-sv-${r.project}`}
                        title={r.digital_leads != null ? `Digital: ${formatCount(r.digital_site_visits)} SV ÷ ${formatCount(r.digital_leads)} leads` : undefined}>
                        {ratio(r.lead_to_sv)}
                      </td>
                      <td className="px-3 py-2.5 num text-sm font-medium">{ratio(r.sv_to_booking)}</td>
                      <td className="px-3 py-2.5 num text-sm font-medium text-[hsl(var(--primary))]">{ratio(r.lead_to_booking)}</td>
                      <td className="px-3 py-2.5 num text-sm text-muted-foreground">{m(r.performance_spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[10px] text-muted-foreground" data-testid="project-conversion-note">
              <Target className="h-3 w-3" /> Lead → SV is measured on Digital performance only — ATL/BTL &amp; CX carry no meaningful lead volume across projects.
            </p>
          </>
        )}
      </div>

      {/* per-vertical table: efficiency ratios (default) or ₹ costs (toggle) */}
      <div className="p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Gauge className="h-3.5 w-3.5" /> {vmode === "ratios" ? "Efficiency by performance vertical" : "Cost efficiency by performance vertical"}</p>
          <ToggleGroup type="single" value={vmode} onValueChange={(v) => v && setVmode(v)} className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="cost-efficiency-mode-toggle">
            <ToggleGroupItem value="ratios" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="cost-efficiency-mode-ratios">Ratios</ToggleGroupItem>
            <ToggleGroupItem value="costs" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="cost-efficiency-mode-costs">Costs (₹)</ToggleGroupItem>
          </ToggleGroup>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="No performance funnel data" message="Add leads / site visits / bookings per vertical to compute ratios." />
        ) : (
          <div className="table-scroll">
            <table className="w-full border-collapse" data-testid="cost-efficiency-matrix">
              <thead><tr>
                <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Vertical</th>
                <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Perf. Spend</th>
                {(vmode === "ratios" ? ratioMetrics : costMetrics).map((mt) => <th key={mt.key} className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{mt.label}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.vertical} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`} data-testid={`cost-efficiency-row-${r.vertical}`}>
                    <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">
                      <span className="inline-flex items-center gap-1.5">{r.vertical}
                        <span className="text-[10px] text-muted-foreground">{formatCount(r.leads)} L · {formatCount(r.site_visits)} SV · {formatCount(r.bookings)} B</span>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 num text-sm text-muted-foreground">{m(r.performance_spend)}</td>
                    {vmode === "ratios"
                      ? ratioMetrics.map((mt) => <td key={mt.key} className="px-3 py-2.5 num text-sm font-medium" data-testid={`cost-efficiency-${mt.key}-${r.vertical}`}>{mt.fmt(r[mt.key])}</td>)
                      : costMetrics.map((mt) => <td key={mt.key} className="px-3 py-2.5 num text-sm font-medium" data-testid={`cost-efficiency-${mt.key}-${r.vertical}`}>{m(r[mt.key])}</td>)}
                  </tr>
                ))}
                <tr className="border-t-2 bg-[hsl(var(--surface-2))] font-semibold" data-testid="cost-efficiency-row-overall">
                  <td className="td-sticky-left px-3 py-2.5 text-sm">All performance</td>
                  <td className="px-3 py-2.5 num text-sm">{m(overall.performance_spend)}</td>
                  {vmode === "ratios"
                    ? ratioMetrics.map((mt) => <td key={mt.key} className="px-3 py-2.5 num text-sm" data-testid={`cost-efficiency-${mt.key}-overall`}>{mt.fmt(overall[mt.key])}</td>)
                    : costMetrics.map((mt) => <td key={mt.key} className="px-3 py-2.5 num text-sm" data-testid={`cost-efficiency-${mt.key}-overall`}>{m(overall[mt.key])}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
